<template>
  <section class="d-grid gap-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h4 mb-1">Series fiscales</h2>
        <p class="text-muted mb-0">Vincula timbrado, establecimiento y punto de expedición con su numeración operativa.</p>
      </div>
      <button class="btn btn-outline-dark btn-sm" @click="loadAll" :disabled="loading">Recargar</button>
    </header>

    <div class="card shadow-sm">
      <div class="card-body">
        <form class="row g-3" @submit.prevent="saveRow">
          <div class="col-md-3">
            <label class="form-label">Timbrado</label>
            <select v-model.number="form.timbrado_id" class="form-select">
              <option v-for="row in timbrados" :key="row.timbrado_id" :value="row.timbrado_id">
                {{ row.numero_timbrado || row.timbrado }}
              </option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Establecimiento</label>
            <select v-model.number="form.establecimiento_id" class="form-select" @change="syncPoints">
              <option v-for="row in establishments" :key="row.establecimiento_id" :value="row.establecimiento_id">
                {{ row.codigo_establecimiento }} · {{ row.nombre_establecimiento }}
              </option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Punto</label>
            <select v-model.number="form.punto_id" class="form-select">
              <option v-for="row in availablePoints" :key="row.punto_id" :value="row.punto_id">
                {{ row.codigo_punto_expedicion }} · {{ row.nombre }}
              </option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Siguiente número</label>
            <input v-model.number="form.siguiente_numero" class="form-control" type="number" min="1" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Número desde</label>
            <input v-model.number="form.numero_desde" class="form-control" type="number" min="1" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Número hasta</label>
            <input v-model.number="form.numero_hasta" class="form-control" type="number" min="1" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Activo</label>
            <select v-model="form.activo" class="form-select">
              <option :value="true">Sí</option>
              <option :value="false">No</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Predeterminado</label>
            <select v-model="form.predeterminado" class="form-select">
              <option :value="true">Sí</option>
              <option :value="false">No</option>
            </select>
          </div>
          <div class="col-12 d-flex gap-2">
            <button class="btn btn-dark" :disabled="saving">{{ saving ? 'Guardando...' : submitLabel }}</button>
            <button class="btn btn-outline-secondary" type="button" @click="resetForm">Limpiar</button>
          </div>
        </form>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>ID</th>
              <th>Serie</th>
              <th>Timbrado</th>
              <th>Siguiente</th>
              <th>Rango</th>
              <th>Default</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center text-muted py-4">Cargando...</td>
            </tr>
            <tr v-else-if="!rows.length">
              <td colspan="7" class="text-center text-muted py-4">No hay series registradas.</td>
            </tr>
            <tr v-for="row in rows" :key="row.serie_id">
              <td>{{ row.serie_id }}</td>
              <td>{{ row.codigo_establecimiento }}-{{ row.codigo_punto_expedicion }}</td>
              <td>{{ row.numero_timbrado || row.timbrado }}</td>
              <td>{{ row.siguiente_numero }}</td>
              <td>{{ row.numero_desde || '—' }} / {{ row.numero_hasta || '—' }}</td>
              <td>{{ row.predeterminado ? 'Sí' : 'No' }}</td>
              <td class="text-end d-flex justify-content-end gap-2">
                <button class="btn btn-outline-dark btn-sm" @click="editRow(row)">Editar</button>
                <button class="btn btn-dark btn-sm" @click="setDefault(row)">Predeterminar</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import maintenanceFiscalService from '@/services/maintenanceFiscalService'

const loading = ref(false)
const saving = ref(false)
const rows = ref([])
const timbrados = ref([])
const establishments = ref([])
const points = ref([])
const form = reactive({
  serie_id: null,
  timbrado_id: null,
  establecimiento_id: null,
  punto_id: null,
  siguiente_numero: 1,
  numero_desde: null,
  numero_hasta: null,
  activo: true,
  predeterminado: false
})

const submitLabel = computed(() => form.serie_id ? 'Actualizar serie' : 'Crear serie')
const availablePoints = computed(() => points.value.filter((row) => Number(row.establecimiento_id) === Number(form.establecimiento_id)))

function resetForm() {
  form.serie_id = null
  form.timbrado_id = timbrados.value[0]?.timbrado_id || null
  form.establecimiento_id = establishments.value[0]?.establecimiento_id || null
  syncPoints()
  form.siguiente_numero = 1
  form.numero_desde = null
  form.numero_hasta = null
  form.activo = true
  form.predeterminado = false
}

function syncPoints() {
  const firstPoint = availablePoints.value[0]
  if (!availablePoints.value.some((row) => Number(row.punto_id) === Number(form.punto_id))) {
    form.punto_id = firstPoint?.punto_id || null
  }
}

function editRow(row) {
  form.serie_id = row.serie_id
  form.timbrado_id = row.timbrado_id
  form.establecimiento_id = row.establecimiento_id
  form.punto_id = row.punto_id
  form.siguiente_numero = Number(row.siguiente_numero || 1)
  form.numero_desde = row.numero_desde != null ? Number(row.numero_desde) : null
  form.numero_hasta = row.numero_hasta != null ? Number(row.numero_hasta) : null
  form.activo = row.activo !== false
  form.predeterminado = row.predeterminado === true
}

async function loadAll() {
  loading.value = true
  try {
    const [timbradoRows, establishmentRows, seriesRows] = await Promise.all([
      maintenanceFiscalService.listTimbrados(),
      maintenanceFiscalService.listEstablishments(),
      maintenanceFiscalService.listSeries()
    ])
    timbrados.value = timbradoRows || []
    establishments.value = establishmentRows || []
    rows.value = seriesRows || []
    points.value = []
    for (const establishment of establishments.value) {
      const establishmentPoints = await maintenanceFiscalService.listPoints(establishment.establecimiento_id)
      points.value.push(...(establishmentPoints || []))
    }
    resetForm()
  } catch (error) {
    console.error('Error cargando series', error)
    window.alert(error.userMessage || 'No se pudieron cargar las series')
  } finally {
    loading.value = false
  }
}

async function saveRow() {
  saving.value = true
  try {
    const payload = {
      timbrado_id: form.timbrado_id,
      establecimiento_id: form.establecimiento_id,
      punto_id: form.punto_id,
      siguiente_numero: form.siguiente_numero,
      numero_desde: form.numero_desde,
      numero_hasta: form.numero_hasta,
      activo: form.activo,
      predeterminado: form.predeterminado
    }
    if (form.serie_id) {
      await maintenanceFiscalService.updateSeries(form.serie_id, payload)
      if (form.predeterminado) {
        await maintenanceFiscalService.setDefaultSeries(form.serie_id)
      }
    } else {
      const created = await maintenanceFiscalService.createSeries(payload)
      if (form.predeterminado && created?.serie_id) {
        await maintenanceFiscalService.setDefaultSeries(created.serie_id)
      }
    }
    await loadAll()
  } catch (error) {
    console.error('Error guardando serie', error)
    window.alert(error.userMessage || 'No se pudo guardar la serie')
  } finally {
    saving.value = false
  }
}

async function setDefault(row) {
  try {
    await maintenanceFiscalService.setDefaultSeries(row.serie_id)
    await loadAll()
  } catch (error) {
    console.error('Error definiendo serie predeterminada', error)
    window.alert(error.userMessage || 'No se pudo definir la serie predeterminada')
  }
}

onMounted(loadAll)
</script>
